import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { CloseIconSVG } from 'src/assets/svg';
import { Button, buttonClassName } from 'src/components/Buttons/Button';
import { Modal } from 'src/components/Modals/Modal';
import { PreviewDocument, useDocumentPreview } from './useDocumentPreview';

const PREVIEW_IMAGE_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/bmp',
]);

type Props = {
    organizationId: string;
    item: PreviewDocument;
    onClose: () => void;
};

export const OrganizationDocumentPreviewModal = ({
    organizationId,
    item,
    onClose,
}: Props) => {
    const { t } = useTranslation();
    const { state, retry } = useDocumentPreview(organizationId, item);
    const [renderFailed, setRenderFailed] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);
    const title =
        item.document.displayName?.trim() || item.document.originalName;
    const isPdf = item.document.mimeType === 'application/pdf';
    const isImage = PREVIEW_IMAGE_TYPES.has(item.document.mimeType);
    const linkClass = (active = false) =>
        cx(
            'inline-flex items-center',
            buttonClassName({ active, size: 'comfortable' }),
        );

    useEffect(() => {
        const previousFocus = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();
        return () => {
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus();
        };
    }, []);

    return (
        <Modal
            show
            className="fixed inset-0 z-70 flex items-center justify-center bg-black/80 p-3 sm:p-6"
            onClick={event => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                tabIndex={-1}
                className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-30px bg-lightPeach text-woodyBrown shadow-xl outline-none"
                onKeyDown={event => {
                    if (event.key === 'Escape') onClose();
                    if (event.key !== 'Tab') return;
                    const controls =
                        dialogRef.current?.querySelectorAll<HTMLElement>(
                            'button, a[href], iframe',
                        );
                    if (!controls?.length) return;
                    const first = controls[0];
                    const last = controls[controls.length - 1];
                    if (
                        event.shiftKey &&
                        (document.activeElement === first ||
                            document.activeElement === dialogRef.current)
                    ) {
                        event.preventDefault();
                        last.focus();
                    } else if (
                        !event.shiftKey &&
                        document.activeElement === last
                    ) {
                        event.preventDefault();
                        first.focus();
                    }
                }}
            >
                <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-woodyBrown/15 px-5 py-5 sm:px-8">
                    <div className="min-w-0 flex-1">
                        <h2
                            className="truncate text-xl font-semibold"
                            title={title}
                        >
                            {title}
                        </h2>
                        {title !== item.document.originalName && (
                            <p className="truncate text-sm text-woodyBrown/60">
                                {item.document.originalName}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        aria-label={t('admin.documents.previewClose')}
                        onClick={onClose}
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-woodyBrown transition-colors hover:bg-woodyBrown hover:text-lightPeach focus:outline-none"
                    >
                        <CloseIconSVG className="h-3 w-3" />
                    </button>
                </header>
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-auto px-5 py-5 sm:px-8">
                    {state.status === 'loading' && (
                        <p role="status">
                            {t('admin.documents.previewLoading')}
                        </p>
                    )}
                    {state.status === 'error' && (
                        <>
                            <p role="alert">
                                {t('admin.documents.previewError')}
                            </p>
                            <Button
                                title={t('admin.documents.previewRetry')}
                                onClick={retry}
                                size="comfortable"
                            />
                        </>
                    )}
                    {state.status === 'ready' && (
                        <>
                            {renderFailed ? (
                                <>
                                    <p role="alert">
                                        {t(
                                            'admin.documents.previewRenderError',
                                        )}
                                    </p>
                                    <Button
                                        title={t(
                                            'admin.documents.previewRetry',
                                        )}
                                        onClick={() => {
                                            setRenderFailed(false);
                                            retry();
                                        }}
                                        size="comfortable"
                                    />
                                </>
                            ) : isPdf ? (
                                <>
                                    <p className="text-sm text-woodyBrown/60">
                                        {t('admin.documents.previewPdfHint')}
                                    </p>
                                    <iframe
                                        title={title}
                                        src={state.url}
                                        className="min-h-0 w-full flex-1 rounded-2xl bg-white shadow-sm"
                                        onError={() => setRenderFailed(true)}
                                    />
                                </>
                            ) : isImage ? (
                                <img
                                    src={state.url}
                                    alt={title}
                                    className="min-h-0 max-h-full max-w-full rounded-2xl object-contain shadow-sm"
                                    onError={() => setRenderFailed(true)}
                                />
                            ) : (
                                <p>{t('admin.documents.previewUnsupported')}</p>
                            )}
                        </>
                    )}
                </div>
                <footer className="flex flex-shrink-0 flex-wrap justify-end gap-3 border-t border-woodyBrown/15 bg-lightPeach px-5 py-4 sm:px-8">
                    <Button
                        title={t('admin.documents.previewClose')}
                        onClick={onClose}
                        size="comfortable"
                    />
                    {state.status === 'ready' && (
                        <>
                            {(isPdf || isImage) && (
                                <a
                                    className={linkClass()}
                                    href={state.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('admin.documents.previewOpenTab')}
                                </a>
                            )}
                            <a
                                className={linkClass(true)}
                                href={state.url}
                                download={item.document.originalName}
                            >
                                {t('admin.documents.previewDownload')}
                            </a>
                        </>
                    )}
                </footer>
            </div>
        </Modal>
    );
};
